import { z } from 'zod'

import {
  developerSystemAnalyticsSchema,
  developerUsersPageSchema,
  developerWorkspaceDetailSchema,
  developerWorkspacePageSchema,
  developerWorkspaceSchema,
  forceSetSubscriptionRequestSchema,
  type DeveloperSystemAnalytics,
  type DeveloperUsersPage,
  type DeveloperWorkspace,
  type DeveloperWorkspaceDetail,
  type DeveloperWorkspacePage,
  type ForceSetSubscriptionRequest,
} from '@/types/api/developer'
import { lockedResourceSchema, subscriptionPlanSchema, subscriptionStatusSchema } from '@/types/api/subscription'

import { apiClient } from './client'


export async function getDeveloperWorkspaces(): Promise<DeveloperWorkspace[]> {
  const response = await apiClient.get('/developer/workspaces')
  return z.array(developerWorkspaceSchema).parse(response.data)
}

export async function getDeveloperWorkspace(workspaceId: string): Promise<DeveloperWorkspaceDetail> {
  const response = await apiClient.get(`/developer/workspaces/${workspaceId}`)
  return developerWorkspaceDetailSchema.parse(response.data)
}

export async function getDeveloperWorkspaceDetail(workspaceId: string): Promise<DeveloperWorkspaceDetail> {
  const response = await apiClient.get(`/developer/workspaces/${workspaceId}`)
  return developerWorkspaceDetailSchema.parse(response.data)
}

export async function getDeveloperWorkspacePage(params: {
  search?: string
  status?: string
  planCode?: string
  page?: number
  size?: number
}): Promise<DeveloperWorkspacePage> {
  const response = await apiClient.get('/developer/workspaces/search', { params })
  return developerWorkspacePageSchema.parse(response.data)
}

export async function getDeveloperWorkspaceSubscription(workspaceId: string) {
  const response = await apiClient.get(`/developer/workspaces/${workspaceId}/subscription`)
  return subscriptionStatusSchema.parse(response.data)
}

export async function forceSetSubscription(
  workspaceId: string,
  data: ForceSetSubscriptionRequest
): Promise<void> {
  const validated = forceSetSubscriptionRequestSchema.parse(data)
  await apiClient.put(`/developer/workspaces/${workspaceId}/subscription`, validated)
}

export async function unlockAllResources(workspaceId: string): Promise<void> {
  await apiClient.post(`/developer/workspaces/${workspaceId}/subscription/unlock-all`)
}

export async function getDeveloperLockedResources(workspaceId: string): Promise<unknown> {
  const response = await apiClient.get(`/developer/workspaces/${workspaceId}/subscription/locked-resources`)
  return z.array(lockedResourceSchema).parse(response.data)
}

export async function getDeveloperPlans() {
  const response = await apiClient.get('/developer/plans')
  return z.array(subscriptionPlanSchema).parse(response.data)
}

export async function getDeveloperUsersPage(params: {
  search?: string
  workspaceId?: string
  page?: number
  size?: number
}): Promise<DeveloperUsersPage> {
  const response = await apiClient.get('/developer/users', { params })
  return developerUsersPageSchema.parse(response.data)
}

export async function getDeveloperAnalytics(): Promise<DeveloperSystemAnalytics> {
  const response = await apiClient.get('/developer/analytics')
  return developerSystemAnalyticsSchema.parse(response.data)
}